import React, { useState, useRef, useEffect, useCallback } from "react";
import styled from "styled-components";
import { colors } from "utils";
import { ArrowDownIcon } from "../Icons";

export const DeviceDropdown = ({ icon, devices, selectedDeviceId, onSelect, fallbackLabel, dropDown }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);
  const itemRefs = useRef([]);

  const selectedDevice = devices.find((d) => d.deviceId === selectedDeviceId);
  const displayLabel = selectedDevice?.label || fallbackLabel;
  const hasOptions = devices.length > 1;

  const handleClose = useCallback((e) => {
    if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
      setIsOpen(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener("mousedown", handleClose);
      return () => document.removeEventListener("mousedown", handleClose);
    }
  }, [isOpen, handleClose]);

  useEffect(() => {
    if (isOpen && activeIndex >= 0) {
      itemRefs.current[activeIndex]?.focus();
    }
  }, [isOpen, activeIndex]);

  const openMenu = () => {
    if (!hasOptions) return;
    const selectedIndex = devices.findIndex((d) => d.deviceId === selectedDeviceId);
    setActiveIndex(selectedIndex >= 0 ? selectedIndex : 0);
    setIsOpen(true);
  };

  const closeMenu = ({ returnFocus = true } = {}) => {
    setIsOpen(false);
    setActiveIndex(-1);
    if (returnFocus) triggerRef.current?.focus();
  };

  const handleSelect = (deviceId) => {
    onSelect(deviceId);
    closeMenu();
  };

  const handleTriggerKeyDown = (e) => {
    if (!hasOptions) return;
    if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      isOpen ? setActiveIndex((i) => Math.min(i + 1, devices.length - 1)) : openMenu();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      openMenu();
    }
  };

  const handleItemKeyDown = (e, index) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex(Math.min(index + 1, devices.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex(Math.max(index - 1, 0));
    } else if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleSelect(devices[index].deviceId);
    } else if (e.key === "Escape") {
      e.preventDefault();
      closeMenu();
    } else if (e.key === "Tab") {
      closeMenu({ returnFocus: false });
    }
  };

  return (
    <DropdownWrapper ref={dropdownRef}>
      {isOpen && hasOptions && (
        <DropdownPanel $dropDown={dropDown} role="listbox">
          {devices.map((device, index) => (
            <DropdownItem
              key={device.deviceId}
              ref={(el) => (itemRefs.current[index] = el)}
              type="button"
              role="option"
              aria-selected={device.deviceId === selectedDeviceId}
              $active={device.deviceId === selectedDeviceId}
              onClick={() => handleSelect(device.deviceId)}
              onKeyDown={(e) => handleItemKeyDown(e, index)}
            >
              {device.label}
            </DropdownItem>
          ))}
        </DropdownPanel>
      )}
      <PillTrigger
        ref={triggerRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onClick={() => (isOpen ? closeMenu({ returnFocus: false }) : openMenu())}
        onKeyDown={handleTriggerKeyDown}
      >
        <PillContent>
          <PillLeft>
            {icon}
            <PillLabel>{displayLabel}</PillLabel>
          </PillLeft>
          {hasOptions && <ArrowDownIcon />}
        </PillContent>
      </PillTrigger>
    </DropdownWrapper>
  );
};

const DropdownWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  position: relative;
  width: 100%;
`;

const DropdownPanel = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0px 4px 16px rgba(0, 0, 0, 0.12);
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
  position: absolute;
  ${({ $dropDown }) => $dropDown ? 'top: calc(100% + 4px)' : 'bottom: calc(100% + 4px)'};
  left: 0;
  min-width: 240px;
  /* Above the page logo (inline z-index 1000 on the Join screen). */
  z-index: 1001;
  /* Long device lists on short screens scroll inside the panel instead of
     growing past the viewport edge. */
  max-height: calc(50vh - 40px);
  overflow-y: auto;
`;

const DropdownItem = styled.button`
  background: none;
  border: none;
  text-align: left;
  width: 100%;
  color: ${colors.nightlyWoods500};
  font-family: 'Roboto', sans-serif;
  font-size: 14px;
  font-weight: ${({ $active }) => ($active ? 600 : 400)};
  line-height: 20px;
  padding: 4px 0;
  cursor: pointer;
  transition: opacity 0.15s ease;

  &:hover {
    opacity: 0.7;
  }
`;

const PillTrigger = styled.button`
  align-items: center;
  background-color: ${colors.milkGlass600};
  border-radius: 9999px;
  border: none;
  display: flex;
  gap: 8px;
  padding: 8px 16px;
  width: 100%;
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: ${colors.milkGlass800};
  }
`;

const PillContent = styled.div`
  align-items: center;
  display: flex;
  flex: 1;
  justify-content: space-between;
  min-width: 0;
  gap: 4px;
`;

const PillLeft = styled.div`
  align-items: center;
  display: flex;
  gap: 4px;
  overflow: hidden;

  svg {
    flex-shrink: 0;
  }
`;

const PillLabel = styled.span`
  color: ${colors.nightlyWoods500};
  font-family: 'Roboto', sans-serif;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0;
  line-height: 26px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`;
